import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthStore } from '@core/store/core/auth.store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, take, throwError } from 'rxjs';

/**
 * Attaches a Bearer token to requests targeting our own APIs and handles
 * the silent token renewal race condition.
 *
 * Flow:
 *  1. Always read the token live from OidcSecurityService so scheduled silent
 *     renewals are transparent — no stale cached token in the store.
 *  2. On a 401 response, explicitly call forceRefreshSession() to obtain a
 *     fresh access token (OAuth2 refresh-token grant), then retry the request
 *     exactly once with that new token.
 *  3. If the refresh or the retry fails, call handleSessionLoss() which clears
 *     the auth store and redirects to /sign-in. Using the store method (rather
 *     than a bare router.navigate) ensures the signal store is reset atomically
 *     so the AuthenticationGuard cannot read a stale isAuthenticated === true
 *     and immediately bounce the user back out of the sign-in page.
 *
 * Historical note: a previous version filtered on `isAuthenticated$` before
 * retrying. That was buggy because `isAuthenticated$` is a BehaviorSubject
 * whose current value is almost always `true`, so the retry used the SAME
 * stale token that just 401'd, guaranteeing a second 401 and a forced logout.
 */
export const httpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes(environment.api) && !req.url.includes(environment.mmscanApiUrl)) {
    return next(req);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const authStore = inject(AuthStore);

  const addToken = (token: string) => req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });

  return oidcSecurityService.getAccessToken().pipe(
    take(1),
    switchMap((token) => next(addToken(token))),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      // 401 — force a refresh-token exchange, then retry once with the
      // brand-new access token it produces.
      return oidcSecurityService.forceRefreshSession().pipe(
        take(1),
        switchMap((result) => {
          const freshToken = result?.accessToken;
          if (!freshToken) {
            authStore.handleSessionLoss();
            return throwError(() => error);
          }
          return next(addToken(freshToken));
        }),
        catchError(() => {
          // Refresh or retry failed — session is truly gone.
          authStore.handleSessionLoss();
          return throwError(() => error);
        })
      );
    })
  );
};
